import type { AgentWsMessage } from '../types'
import { useState, useEffect, useRef, useCallback } from 'react'

export const API_URL = import.meta.env.VITE_API_URL || ''
export const API_HOST = import.meta.env.VITE_API_HOST || window.location.hostname
export const API_PORT = import.meta.env.VITE_API_PORT || ''
export const WS_URL = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${API_HOST}${API_PORT ? ':' + API_PORT : ''}/ws`

export function useAtlasAgent({ onResponse, onStatus, onError, onModel }: {
  onResponse?: (data: AgentWsMessage) => void
  onStatus?: (data: AgentWsMessage) => void
  onError?: (data: AgentWsMessage) => void
  onModel?: (model?: string) => void
}) {
  const socketRef = useRef<WebSocket | null>(null)
  const [connectionStatus, setConnectionStatus] = useState('desconectado')
  const [modelStatus, setModelStatus] = useState('Modelo: -')

  const setupWebSocket = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) return
    try {
      socketRef.current = new WebSocket(WS_URL)
      socketRef.current.onopen = () => {
        setConnectionStatus('conectado')
      }
      socketRef.current.onerror = err => {
        console.error('WebSocket error:', err)
      }
      socketRef.current.onclose = event => {
        setConnectionStatus('desconectado')
        const delay = event.code === 1006 ? 1000 : 3000
        setTimeout(setupWebSocket, delay)
      }
      socketRef.current.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'PING') {
            socketRef.current?.send(JSON.stringify({ type: 'PONG' }))
            return
          }
          if (data.type === 'AGENT_STATUS') {
            onStatus?.(data)
          } else if (data.type === 'RESPONSE' || data.type === 'CLARIFICATION') {
            setModelStatus(data.model ? `Modelo: ${data.model}` : modelStatus)
            onModel?.(data.model)
            onResponse?.(data)
          } else if (data.error) {
            onError?.(data)
          }
        } catch (e: unknown) {
          console.error('Erro ao parsear mensagem:', e)
        }
      }
    } catch (e: unknown) {
      setTimeout(setupWebSocket, 3000)
    }
  }, [onResponse, onStatus, onError, onModel, modelStatus])

  useEffect(() => {
    setupWebSocket()
    return () => {
      socketRef.current?.close()
    }
  }, [setupWebSocket])

  const send = useCallback((payload: AgentWsMessage) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(payload))
      return true
    }
    return false
  }, [])

  const cancel = useCallback(() => {
    send({ type: 'CANCEL' })
  }, [send])

  const sendPrompt = useCallback(({
    prompt,
    audioBase64,
    videoBase64,
    location,
    ttsProvider = 'edge'
  }: { prompt: string; audioBase64?: string; videoBase64?: string; location?: string; ttsProvider?: string }) => {
    return send({
      type: 'MESSAGE',
      prompt,
      audio: audioBase64,
      video: videoBase64,
      location,
      ttsProvider
    })
  }, [send])

  return { connectionStatus, modelStatus, sendPrompt, cancel, send }
}
